import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Building2, Mail, Phone, User, Car, MapPin, CheckCircle, ArrowRight } from 'lucide-react';
import { API_BASE_URL } from '../config';

const API = API_BASE_URL + '/api';
const CORPORATE_API_KEY = 'corp_api_key_alvorubon_2024_secure';

const FLEET_SIZES = ['1-5 bílar', '6-10 bílar', '11-20 bílar', '21-50 bílar', '51-100 bílar', '100+ bílar'];
const VEHICLE_TYPES = [
  'Fólksbílar',
  'Jeppastæðar / SUV',
  'Sendibílar / Bifreiðar',
  'Blönduð bílafloti',
  'Annað'
];

function CorporateApplicationForm() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    company_name: '',
    kennitala: '',
    company_address: '',
    company_email: '',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    estimated_fleet_size: '',
    vehicle_types: '',
    notes: ''
  });
  const [errors, setErrors] = useState({});

  const update = (field, value) => {
    setFormData(f => ({ ...f, [field]: value }));
    if (errors[field]) setErrors(e => ({ ...e, [field]: '' }));
  };

  const validateStep1 = () => {
    const e = {};
    if (!formData.company_name.trim()) e.company_name = 'Nafn fyrirtækis er nauðsynlegt';
    const kt = formData.kennitala.replace(/[^0-9]/g, '');
    if (kt.length !== 10) e.kennitala = 'Kennitala þarf að vera 10 tölustafir';
    if (!formData.estimated_fleet_size) e.estimated_fleet_size = 'Veldu fjölda bíla';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const validateStep2 = () => {
    const e = {};
    if (!formData.contact_name.trim()) e.contact_name = 'Nafn tengiliðs er nauðsynlegt';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contact_email)) e.contact_email = 'Gilt netfang er nauðsynlegt';
    if (!formData.contact_phone.trim()) e.contact_phone = 'Símanúmer er nauðsynlegt';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleNext = () => {
    if (step === 1 && validateStep1()) setStep(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep2()) return;

    const cleanKt = formData.kennitala.replace(/[^0-9]/g, '');
    try {
      setSubmitting(true);
      const res = await axios.post(
        `${API}/portal/corporate/applications`,
        { ...formData, kennitala: cleanKt },
        { headers: { 'X-API-Key': CORPORATE_API_KEY } }
      );
      if (res.data.success) {
        setSubmitted(true);
        toast.success('Umsókn móttekin!');
      }
    } catch {
      toast.error('Villa kom upp við að senda umsókn. Reyndu aftur.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 flex items-center justify-center p-4">
        <Card className="max-w-xl w-full shadow-2xl border-0 overflow-hidden">
          <div className="bg-gradient-to-r from-green-500 to-emerald-600 p-8 text-center text-white">
            <div className="mx-auto w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mb-4">
              <CheckCircle className="h-12 w-12 text-white" />
            </div>
            <h1 className="text-3xl font-bold">Umsókn móttekin!</h1>
            <p className="text-green-100 mt-2">Takk fyrir að hafa samband við okkur</p>
          </div>
          <CardContent className="p-8 space-y-5">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="font-semibold text-blue-900 mb-2">Hvað gerist næst?</p>
              <ul className="space-y-2 text-sm text-blue-800">
                <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />Við förum yfir umsóknina innan 1-2 virkra daga</li>
                <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />Þú færð tölvupóst með niðurstöðu og stillingum</li>
                <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />Við setjum upp greiðslufyrirkomulag og afslætti</li>
              </ul>
            </div>
            <div className="text-center text-sm text-slate-500">
              Spurningar? <a href="mailto:alvorubon@alvorubon.is" className="text-blue-600 underline">alvorubon@alvorubon.is</a>
            </div>
            <Button variant="outline" className="w-full" onClick={() => window.location.href = '/'}>Til baka á forsíðu</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">

        {/* Logo / header */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-white/10 rounded-2xl mb-3">
            <Building2 className="h-9 w-9 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-white">Fyrirtækjaþjónusta</h1>
          <p className="text-blue-200 mt-1">Sæktu um fyrirtækjasamning hjá Alvörubón</p>
        </div>

        {/* Step indicator */}
        <div className="flex items-center justify-center gap-3 mb-6">
          {[1, 2].map(s => (
            <React.Fragment key={s}>
              <div className={`flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-medium transition-all ${step === s ? 'bg-white text-blue-900' : step > s ? 'bg-green-500 text-white' : 'bg-white/10 text-white/50'}`}>
                {step > s ? <CheckCircle className="h-4 w-4" /> : <span className="w-4 h-4 flex items-center justify-center">{s}</span>}
                {s === 1 ? 'Fyrirtæki' : 'Tengiliður'}
              </div>
              {s < 2 && <div className={`h-px w-8 ${step > 1 ? 'bg-green-400' : 'bg-white/20'}`} />}
            </React.Fragment>
          ))}
        </div>

        <Card className="shadow-2xl border-0">
          <CardContent className="p-8">
            <form onSubmit={handleSubmit}>

              {step === 1 && (
                <div className="space-y-5">
                  <div>
                    <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2 mb-4">
                      <Building2 className="h-5 w-5 text-blue-600" />Upplýsingar um fyrirtæki
                    </h2>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="sm:col-span-2">
                      <Label>Nafn fyrirtækis *</Label>
                      <Input placeholder="Alvörufyrirtæki ehf." value={formData.company_name} onChange={e => update('company_name', e.target.value)} className={errors.company_name ? 'border-red-400' : ''} data-testid="company-name" />
                      {errors.company_name && <p className="text-red-500 text-xs mt-1">{errors.company_name}</p>}
                    </div>

                    <div>
                      <Label>Kennitala fyrirtækis *</Label>
                      <Input placeholder="1234567890" value={formData.kennitala} onChange={e => update('kennitala', e.target.value)} maxLength={11} className={errors.kennitala ? 'border-red-400' : ''} data-testid="company-kennitala" />
                      {errors.kennitala ? <p className="text-red-500 text-xs mt-1">{errors.kennitala}</p> : <p className="text-xs text-slate-400 mt-1">10 tölustafir</p>}
                    </div>

                    <div>
                      <Label>Netfang fyrirtækis</Label>
                      <Input type="email" placeholder="reikningar@fyrirtaeki.is" value={formData.company_email} onChange={e => update('company_email', e.target.value)} data-testid="company-email" />
                    </div>

                    <div className="sm:col-span-2">
                      <Label className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" />Heimilisfang</Label>
                      <Input placeholder="Laugavegur 1, 101 Reykjavík" value={formData.company_address} onChange={e => update('company_address', e.target.value)} data-testid="company-address" />
                    </div>
                  </div>

                  <div className="border-t pt-4 space-y-4">
                    <h3 className="font-semibold text-slate-700 flex items-center gap-1.5"><Car className="h-4 w-4 text-blue-500" />Bílafloti</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Label>Fjöldi bíla *</Label>
                        <Select value={formData.estimated_fleet_size} onValueChange={v => update('estimated_fleet_size', v)}>
                          <SelectTrigger className={errors.estimated_fleet_size ? 'border-red-400' : ''} data-testid="fleet-size">
                            <SelectValue placeholder="Veldu fjölda" />
                          </SelectTrigger>
                          <SelectContent>
                            {FLEET_SIZES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        {errors.estimated_fleet_size && <p className="text-red-500 text-xs mt-1">{errors.estimated_fleet_size}</p>}
                      </div>
                      <div>
                        <Label>Tegund bíla</Label>
                        <Select value={formData.vehicle_types} onValueChange={v => update('vehicle_types', v)}>
                          <SelectTrigger data-testid="vehicle-types">
                            <SelectValue placeholder="Veldu tegund" />
                          </SelectTrigger>
                          <SelectContent>
                            {VEHICLE_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>

                  <div>
                    <Label>Athugasemdir (valkvætt)</Label>
                    <Textarea placeholder="Sérstakar þarfir, þjónustuþarfir, eða aðrar upplýsingar..." value={formData.notes} onChange={e => update('notes', e.target.value)} rows={3} className="resize-none" data-testid="app-notes" />
                  </div>

                  {/* Benefits */}
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-5 border border-blue-100">
                    <p className="font-semibold text-blue-900 mb-3 text-sm">Kostir fyrirtækjaþjónustu:</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {[
                        'Sérstakur afsláttur (5–20%)',
                        'Greiðslufrestur 30–90 dagar',
                        'Sameinaðir mánaðarlegir reikningar',
                        'Forgangsmeðferð á bókunum',
                        'Sérúthlutaður tengiliður',
                        'Skýrslur og yfirlit',
                      ].map(b => (
                        <div key={b} className="flex items-center gap-2 text-sm text-blue-800">
                          <CheckCircle className="h-3.5 w-3.5 text-green-500 flex-shrink-0" />{b}
                        </div>
                      ))}
                    </div>
                  </div>

                  <Button type="button" className="w-full bg-blue-600 hover:bg-blue-700 text-white py-5 text-base font-semibold" onClick={handleNext} data-testid="next-step-btn">
                    Áfram <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </div>
              )}

              {step === 2 && (
                <div className="space-y-5">
                  <div>
                    <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2 mb-1">
                      <User className="h-5 w-5 text-blue-600" />Tengiliðsupplýsingar
                    </h2>
                    <p className="text-sm text-slate-500">Sá aðili sem við munum hafa samband við</p>
                  </div>

                  <div className="bg-slate-50 rounded-lg p-3 border text-sm text-slate-600 flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-blue-500 flex-shrink-0" />
                    <span className="font-medium">{formData.company_name}</span>
                  </div>

                  <div>
                    <Label>Nafn *</Label>
                    <Input placeholder="Jón Jónsson" value={formData.contact_name} onChange={e => update('contact_name', e.target.value)} className={errors.contact_name ? 'border-red-400' : ''} data-testid="contact-name" />
                    {errors.contact_name && <p className="text-red-500 text-xs mt-1">{errors.contact_name}</p>}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" />Netfang *</Label>
                      <Input type="email" placeholder="jon@fyrirtaeki.is" value={formData.contact_email} onChange={e => update('contact_email', e.target.value)} className={errors.contact_email ? 'border-red-400' : ''} data-testid="contact-email" />
                      {errors.contact_email && <p className="text-red-500 text-xs mt-1">{errors.contact_email}</p>}
                    </div>
                    <div>
                      <Label className="flex items-center gap-1.5"><Phone className="h-3.5 w-3.5" />Símanúmer *</Label>
                      <Input placeholder="5551234" value={formData.contact_phone} onChange={e => update('contact_phone', e.target.value)} className={errors.contact_phone ? 'border-red-400' : ''} data-testid="contact-phone" />
                      {errors.contact_phone && <p className="text-red-500 text-xs mt-1">{errors.contact_phone}</p>}
                    </div>
                  </div>

                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
                    Niðurstaða umsóknar verður send á: <strong>{formData.contact_email || '–'}</strong>
                  </div>

                  <div className="flex gap-3 pt-2">
                    <Button type="button" variant="outline" className="flex-1" onClick={() => setStep(1)}>Til baka</Button>
                    <Button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-5 font-semibold" disabled={submitting} data-testid="submit-application-btn">
                      {submitting
                        ? <><div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2" />Sendir...</>
                        : <>Senda umsókn <ArrowRight className="h-4 w-4 ml-2" /></>}
                    </Button>
                  </div>
                  <p className="text-xs text-center text-slate-400">* Nauðsynlegir reitir</p>
                </div>
              )}
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default CorporateApplicationForm;
